import React from "react";
import { Form, Select, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import { USER_GROUP_ENUM, USER_GROUP_SIZE, UserGroup } from "..";
import { useTranslation } from "react-i18next";
import { NotificationType } from "../../../../../hooks/api/notifications";

import styles from "./index.module.scss";

export default function NotificationForm(): React.ReactElement {
  const form = Form.useFormInstance();
  const { t } = useTranslation();
  // Watched rather than read once: the count under the select follows the
  // choice, the same way the campaign send screen shows its audience size.
  const group = Form.useWatch<UserGroup>("group", form);
  const groupSize = group ? USER_GROUP_SIZE[group] : undefined;

  const userGroupOptions = Object.entries(USER_GROUP_ENUM).map(
    ([value, obj]) => ({
      value,
      label: obj.text,
    }),
  );
  const notificationTypeOptions = Object.entries(NotificationType).map(
    ([value, label]) => ({
      value,
      label: value,
    }),
  );

  return (
    <>
      <Form.Item
        // The count sits on the label's own line, at its end — not under the
        // field. An `extra` beneath the select would add a line to the form:
        // rendered only once a group is chosen it pushes everything below it
        // down, and reserved with an empty line it leaves a gap under this one
        // field that no other field has.
        label={
          <span className={styles.groupLabel}>
            {t("pages.notificationCenter.userGroup")}
            {groupSize === undefined ? null : (
              <Typography.Text type="secondary">
                {t("pages.notificationCenter.recipientsCount", {
                  count: groupSize,
                  recipients: groupSize.toLocaleString(),
                })}
              </Typography.Text>
            )}
          </span>
        }
        name="group"
        rules={[{ required: true, message: t("validations.userGroup") }]}
      >
        <Select
          placeholder={t("placeholder.select")}
          options={userGroupOptions}
        />
      </Form.Item>
      <Form.Item
        label={t("pages.notificationCenter.notificationType")}
        name="notificationType"
        rules={[{ required: true, message: t("validations.notificationType") }]}
      >
        <Select
          placeholder={t("placeholder.select")}
          options={notificationTypeOptions}
        />
      </Form.Item>
      <Form.Item
        label={t("pages.notificationCenter.title")}
        name="title"
        rules={[
          { required: true },
          {
            min: 3,
            max: 50,
          },
        ]}
      >
        {/* antd's own counter and limit: showCount reads maxLength and renders
            "0/50" under the field, and the field stops accepting input there.
            50 is the limit the form already validated against. */}
        <TextArea
          rows={2}
          showCount
          maxLength={50}
          placeholder={t("placeholder.enterTitle")}
        />
      </Form.Item>

      <Form.Item
        label={t("pages.notificationCenter.message")}
        name="description"
        rules={[
          { required: true },
          {
            min: 3,
            // Raised from 150 to match the counter the design asks for. This is
            // a validation rule the API may also enforce — see
            // DEVELOPER_TASKS.md.
            max: 180,
          },
        ]}
      >
        <TextArea
          rows={4}
          showCount
          maxLength={180}
          placeholder={t("placeholder.enterMessage")}
        />
      </Form.Item>
    </>
  );
}
